import moment from 'moment'
import {
  IncomingMyReviewAppraisalForm,
  IncomingMyReviewKRA,
  IncomingPerformanceRating,
  MyReviewAppraisalFormStatus,
  MyReviewFormStatus,
  MyReviewKPI,
  MyReviewKraKpiIndexes,
  MyReviewUpdateRoleEnum,
  MyReviewUpdateTypeEnum,
  UpdateMyReviewFieldsDTO,
  IncomingAppraisalFormAvgRatingDTO,
  IncomingReviewComment,
} from '../../../types/Performance/MyReview/myReviewTypes'

export const initialMyReviewKRA: IncomingMyReviewKRA = {
  id: -1,
  name: '',
  description: null,
  count: -1,
  designationKraPercentage: -1,
  kpis: [],
}

export const myReviewTableItemBorderBottom = '0.75rem'

export const initialPerformanceRating: IncomingPerformanceRating = {
  label: null,
  id: -1,
  rating: -1,
}

export const individualReviewListFeatureId = 231
export const hierarchyReviewListFeatureId = 230
export const reviewListFeatureId = 39

export const getKpiKraIndexesFromList = (
  kraList: IncomingMyReviewKRA[],
  kraId: number,
  kpiId: number,
): MyReviewKraKpiIndexes => {
  // searching for kra
  const searchedKraIndex = kraList.findIndex((kra) => kra.id === kraId)
  if (searchedKraIndex !== -1) {
    // if kra is found
    //searching for kpi in that kra
    const searchedKpiIndex = kraList[searchedKraIndex].kpis.findIndex(
      (kpi) => kpi.id === kpiId,
    )
    if (searchedKpiIndex !== -1) {
      // if kpi is found
      // returning the indices of the kra and kpi found
      return {
        kraIndex: searchedKraIndex,
        kpiIndex: searchedKpiIndex,
      }
    }
  }
  // don't exist. returing undefined
  return {
    kraIndex: undefined,
    kpiIndex: undefined,
  }
}

export const getFinalRatingValue = (ratingString: number): number | null => {
  return ratingString === initialPerformanceRating.rating ? null : +ratingString
}

export const getFinalCommentsValue = (comments: string): string | null => {
  return comments.trim().length === 0 ? null : comments
}

export const getUpdatedMyReviewKraList = (
  initialKraList: IncomingMyReviewKRA[],
  allData: UpdateMyReviewFieldsDTO,
): IncomingMyReviewKRA[] => {
  // destructuring the incoming data
  const { data, updateType, updateRole } = allData
  const { kraId, kpiId, updatedValue } = data

  // getting the indexes of the updated kpi and its kra
  const { kraIndex, kpiIndex } = getKpiKraIndexesFromList(
    initialKraList,
    kraId,
    kpiId,
  )
  const kraListCopy = [...initialKraList]
  // if the kra and its kpi exist
  if (kraIndex !== undefined && kpiIndex !== undefined) {
    const kpiListCopy = kraListCopy[kraIndex].kpis
    // if we are to update for the employee
    if (updateRole === MyReviewUpdateRoleEnum.employee) {
      // if employee rating is to be updated
      if (updateType === MyReviewUpdateTypeEnum.Rating) {
        kpiListCopy[kpiIndex] = {
          ...kpiListCopy[kpiIndex],
          employeeRating: getFinalRatingValue(+updatedValue),
        }
      } else {
        // if employee comments is to be updated
        kpiListCopy[kpiIndex] = {
          ...kpiListCopy[kpiIndex],
          employeeFeedback: getFinalCommentsValue(updatedValue),
        }
      }
    }
    // if we are to update for manager
    else if (updateRole === MyReviewUpdateRoleEnum.manager) {
      // if manager rating is to be updated
      if (updateType === MyReviewUpdateTypeEnum.Rating) {
        kpiListCopy[kpiIndex] = {
          ...kpiListCopy[kpiIndex],
          managerRating: getFinalRatingValue(+updatedValue),
        }
      } else {
        // if manager comments is to be updated
        kpiListCopy[kpiIndex] = {
          ...kpiListCopy[kpiIndex],
          managerFeedback: getFinalCommentsValue(updatedValue),
        }
      }
    }
    kraListCopy[kraIndex] = {
      ...kraListCopy[kraIndex],
      kpis: kpiListCopy,
    }
    return kraListCopy
  }
  return kraListCopy
}

// it returns true if the conditions are satisfied i.e. the button is enabled
export const checkIfEmployeeSubmitButtonIsEnabled = (
  kraList: IncomingMyReviewKRA[],
): boolean => {
  for (const thisKra of kraList) {
    // checking if any other data that is required is missing or not
    const notSatisfiedKPIs = thisKra.kpis.filter(
      (kpi) =>
        kpi.employeeRating === null ||
        kpi.employeeFeedback === null ||
        kpi.employeeFeedback.trim().length < 50,
    )
    if (notSatisfiedKPIs.length > 0) {
      // some required data is not entered by the employee
      return false
    }
  }
  return true
}

// it returns true if the conditions are satisfied i.e. the button is enabled
export const checkIfManagerSubmitButtonIsEnabled = (
  kraList: IncomingMyReviewKRA[],
): boolean => {
  for (const thisKra of kraList) {
    // checking if any other data that is required is missing or not
    const notSatisfiedKPIs = thisKra.kpis.filter(
      (kpi) =>
        kpi.managerRating === null ||
        kpi.managerFeedback === null ||
        kpi.managerFeedback.trim().length < 50,
    )
    if (notSatisfiedKPIs.length > 0) {
      // some required data is not entered by the manager
      return false
    }
  }
  return true
}

// once the manager has submitted the ratings, the employee can see this even after completion
export const canEmployeeViewAfterManagerSubmit = (
  myReviewFormStatus: string,
  appraisalFormStatus: string | null,
): boolean => {
  return (
    (myReviewFormStatus === MyReviewFormStatus.pendingagreement ||
      myReviewFormStatus === MyReviewFormStatus.openForDiscussion ||
      myReviewFormStatus === MyReviewFormStatus.completed ||
      myReviewFormStatus === MyReviewFormStatus.closed) &&
    appraisalFormStatus !== MyReviewAppraisalFormStatus.NotSubmittedByYou
  )
}

// sorted by the level
export const sortAvgRatingDTOsByLevel = (
  dtos: IncomingAppraisalFormAvgRatingDTO[],
): IncomingAppraisalFormAvgRatingDTO[] => {
  const newArray = [...dtos]
  return newArray.sort((a, b) => a.level - b.level)
}

export const showCloseBtnForManager = (
  formStatus: MyReviewFormStatus,
): boolean => {
  return (
    formStatus === MyReviewFormStatus.submitForEmployee ||
    formStatus === MyReviewFormStatus.pending ||
    formStatus === MyReviewFormStatus.pendingagreement ||
    formStatus === MyReviewFormStatus.openForDiscussion
  )
}

// gives you descending order of the list based on createdDate
export const sortReviewCommentsByDateTime = (
  list: IncomingReviewComment[],
): IncomingReviewComment[] => {
  const sortedList = [...list]
  return sortedList.sort((a, b) => {
    const dateA = moment(a.createdDate, 'DD-MMM-YYYY HH:mm:ss:ZZ')
    const dateB = moment(b.createdDate, 'DD-MMM-YYYY HH:mm:ss:ZZ')
    return dateB.diff(dateA)
  })
}

// returns sorted value for kras by name
export const sortKRAByAlphabeticalOrder = (
  list: IncomingMyReviewKRA[],
): IncomingMyReviewKRA[] => {
  const sortedList = [...list]
  return sortedList.sort((a, b) => a.name.localeCompare(b.name))
}

export const sortKPIByAlphabeticalOrder = (
  list: MyReviewKPI[],
): MyReviewKPI[] => {
  const sortedList = [...list]
  return sortedList.sort((a, b) => a.name.localeCompare(b.name))
}

// this function checks if any kpi is left incomplete by EMPLOYEE.
//Only INCOMPLETE AND NOT FOR "NOT ENTERED" and "COMPLETED"
export const isAnyKPIIncompleteForEmployee = (
  list: IncomingMyReviewKRA[],
): [boolean, string] => {
  const sortedKra = sortKRAByAlphabeticalOrder(list) // must be the same as shown in ui
  for (const kraItem of sortedKra) {
    const filteredItems = kraItem.kpis.filter(
      (kpiItem) =>
        kpiItem.employeeFeedback !== null &&
        kpiItem.employeeFeedback.length < 50,
    )
    if (filteredItems.length > 0) {
      return [true, kraItem.name] // returns the kra name
      // returns kra name in which kpi is incomplete
    }
  }
  return [false, '']
}

// this function checks if any kpi is left incomplete by MANAGER.
//Only INCOMPLETE AND NOT FOR "NOT ENTERED" and "COMPLETED"
export const isAnyKPIIncompleteForManager = (
  list: IncomingMyReviewKRA[],
): [boolean, string] => {
  const sortedKra = sortKRAByAlphabeticalOrder(list) // must be the same as shown in ui
  for (const kraItem of sortedKra) {
    const filteredItems = kraItem.kpis.filter(
      (kpiItem) =>
        kpiItem.managerFeedback !== null && kpiItem.managerFeedback.length < 50,
    )
    if (filteredItems.length > 0) {
      return [true, kraItem.name] // returns the kra name
      // returns kra name in which kpi is incomplete
    }
  }
  return [false, '']
}

// comments input visible to employee on following conditions
export const isRequestDiscussionCommentsVisible = (
  formStatus: MyReviewFormStatus,
): boolean => {
  return (
    formStatus === MyReviewFormStatus.openForDiscussion ||
    formStatus === MyReviewFormStatus.pendingagreement
  )
}

// mappings created where key is the value of api and value is the value to be displayed
// USED IN OTHER COMPONENTS ALSO!! CHANGE WITH "CAUTION"
export const reviewStatusMappings: { [key in MyReviewFormStatus]: string } = {
  [MyReviewFormStatus.completed]: 'Completed',
  [MyReviewFormStatus.closed]: 'Closed',
  [MyReviewFormStatus.openForDiscussion]: 'Needs Discussion',
  [MyReviewFormStatus.pendingagreement]: 'Needs Acknowledgement',
  [MyReviewFormStatus.pending]: 'Review Pending',
  [MyReviewFormStatus.saveForEmployee]: 'Not-Submitted',
  [MyReviewFormStatus.submitForEmployee]: 'Review Pending',
}

// takes the api value and returns the value that is to be displayed to user
// USED IN OTHER COMPONENTS ALSO!! CHANGE WITH 'CAUTION'
export const getValueOfReviewStatusMapping = (keyValue: string): string => {
  const filteredValue = Object.entries(reviewStatusMappings).find(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([key, _]) => key === keyValue,
  )
  if (filteredValue !== undefined) {
    return filteredValue[1]
  }
  return ''
}

// when testing with kras and kpis this function must be used as we are displaying them in certain order
export const getKpisOfKraByKraIndex = (
  list: IncomingMyReviewAppraisalForm,
  kraIndex: number,
): MyReviewKPI[] => {
  const { kpis } = sortKRAByAlphabeticalOrder(list.kra)[kraIndex]
  return sortKPIByAlphabeticalOrder(kpis)
}

export const generateMyReviewTestId = (data: string): string => {
  return `myReview-${data}`
}

export const myReviewTestComments =
  'This is testComment.This is testComment.This is testComment.This is testComment.This is testComment.This is testComment.'

// this function compares the incoming kpis with latest kpis and returns the updated kpis
// this function compares the incoming kpis with latest kpis and returns the updated kpis
// this function compares the incoming kpis with latest kpis and returns the updated kpis
export const getKPIsFromKRAs = (
  originalKraList: IncomingMyReviewKRA[],
  currentKRAList: IncomingMyReviewKRA[],
): MyReviewKPI[] => {
  const sortedOriginalKRAList = sortKRAByAlphabeticalOrder(originalKraList)
  const sortedCurrentKRAList = sortKRAByAlphabeticalOrder(currentKRAList)
  const finalKPIs: MyReviewKPI[] = []
  for (let kraIndex = 0; kraIndex < sortedOriginalKRAList.length; kraIndex++) {
    const latestKRAUpdate = sortedCurrentKRAList[kraIndex]
    const originalKRAKpis = sortedOriginalKRAList[kraIndex].kpis
    for (let i = 0; i < originalKRAKpis.length; i++) {
      if (
        originalKRAKpis[i].managerRating !==
        latestKRAUpdate.kpis[i].managerRating
      ) {
        finalKPIs.push(latestKRAUpdate.kpis[i])
      }
    }
  }
  return finalKPIs
}
